import Commons from '../../../../Services/Common/Common';
import dayjs from 'dayjs';

/**
 * SAC_BASIC 스키마
 * - fields: 폼/그리드에서 공통으로 쓰는 필드 정의
 *   - initial: 초기값
 *   - label: 필드명
 *   - required: 필수 입력 여부
 *   - requiredWhen: 언제 필수인지 조건
 *
 * - mapper: 조회 API payload 생성 규칙
 *   - mapParamsBySchema(mapper, context)로 payload를 만듬
 *   - context에는 userInfo 등이 들어오며(mapParamsBySchema에 기본적으로 정의되어 있음),
 *     화면에서 추가로 필요한 값(custCd 등)을 context로 넘겨줄 수 있다.
 */

export const SAC_BASIC_Schema = {
  fields: {
    grid: {
      chaebun: { initial: '', label: '채번' },
      custCd: { initial: '', label: '사업장코드' },
      proceedingsYear: { initial: '', label: '회의연도' },
      proceedingsTitle: { initial: '', label: '회의제목', required: 'Y' },
      proceedingsDate: { initial: '', label: '회의일시' },
      proceedingsStartTime: { initial: '', label: '시작일시' },
      proceedingsEndTime: { initial: '', label: '종료일시' },
      proceedingsLocation: { initial: '', label: '회의장소', required: 'Y' },
      state: { initial: '개최전', label: '상태' },
    },
    attendee: {
      chaebun: { initial: '', label: '채번' },
      seq: { initial: '', label: '순서' },
      gubun: { initial: '0', label: '구분' }, // 0: 사용자위원, 1: 근로자위원
      id: { initial: '', label: 'ID' },
      rank: { initial: '', label: '직위' },
      name: { initial: '', label: '성명' },
      tel: { initial: '', label: '전화번호' },
      depNm: { initial: '', label: '소속' },
      attendYn: { initial: 'N', label: '참석여부' },
      checkYn: { initial: 'N', label: '확인여부' },
      checkPath: { initial: '', label: '확인경로' },
    },
    agenda: {
      chaebun: { initial: '', label: '채번' },
      seq: { initial: '', label: '순서' },
      agendaName: { initial: '', label: '안건명' },
      resultDiscussion: { initial: '', label: '의결사항' },
      person: { initial: '', label: '담당부서/담당자' },
      processingDeadline: { initial: '', label: '처리기한' },
      proceedingsImage: { initial: '', label: '진행사진' },
      proceedingsImageNm: { initial: '', label: '진행사진명' },
    },
  },

  mapper: {
    search1: {
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      ProceedingsYear: (_, context) =>
        Commons.EncryptAES(context.search?.year || '') || '',
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || '') || '',
    },

    search2: {
      Chaebun: (_, context) =>
        Commons.EncryptAES(context.search?.chaebun || '') || '',
    },

    search3: {
      Chaebun: (_, context) =>
        Commons.EncryptAES(context.search?.chaebun || '') || '',
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || '') || '',
    },

    search4: {
      Chaebun: (_, context) =>
        Commons.EncryptAES(context.search?.chaebun || '') || '',
    },

    search5: {
      UserId: (_, context) => context.userInfo?.userid || '',
      AuthCd: (_, context) =>
        Commons.EncryptAES(context.userInfo?.authCd || ''),
      CoCd: (_, context) => Commons.EncryptAES(context.userInfo?.coCd || ''),
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || '') || '',
    },

    save1: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd || ''),
      ProceedingsYear: (_, context) =>
        Commons.EncryptAES(context.search?.year || ''),
      QuarterlyOrder: () => Commons.EncryptAES(''),
      ProceedingsDate: (row) =>
        Commons.EncryptAES(
          row.proceedingsDate
            ? dayjs(row.proceedingsDate).format('YYYY-MM-DD')
            : dayjs().format('YYYY-MM-DD')
        ),
      ProceedingsStartTime: (row) => {
        // formatTimeForSave에서 이미 "HH:mm" 형식의 문자열로 변환되어 들어옴
        // 단순히 그대로 암호화해서 전송 (예: "19:00", "11:30")
        const timeValue = row.proceedingsStartTime;
        if (!timeValue) return Commons.EncryptAES('');

        // 문자열로 변환 후 trim
        const timeStr = String(timeValue).trim();

        // "HH:mm" 형식이면 그대로 사용
        if (/^\d{1,2}:\d{2}$/.test(timeStr)) {
          // 한 자리 시간도 두 자리로 변환 (예: "9:30" -> "09:30")
          const parts = timeStr.split(':');
          const hours = parts[0].padStart(2, '0');
          const minutes = parts[1];
          return Commons.EncryptAES(`${hours}:${minutes}`);
        }

        // 형식이 맞지 않으면 빈 문자열
        return Commons.EncryptAES('');
      },
      ProceedingsEndTime: (row) => {
        // formatTimeForSave에서 이미 "HH:mm" 형식의 문자열로 변환되어 들어옴
        // 단순히 그대로 암호화해서 전송 (예: "20:00", "12:30")
        const timeValue = row.proceedingsEndTime;
        if (!timeValue) return Commons.EncryptAES('');

        // 문자열로 변환 후 trim
        const timeStr = String(timeValue).trim();

        // "HH:mm" 형식이면 그대로 사용
        if (/^\d{1,2}:\d{2}$/.test(timeStr)) {
          // 한 자리 시간도 두 자리로 변환 (예: "9:30" -> "09:30")
          const parts = timeStr.split(':');
          const hours = parts[0].padStart(2, '0');
          const minutes = parts[1];
          return Commons.EncryptAES(`${hours}:${minutes}`);
        }

        // 형식이 맞지 않으면 빈 문자열
        return Commons.EncryptAES('');
      },
      ProceedingsLocation: (row) =>
        Commons.EncryptAES(row.proceedingsLocation || ''),
      ProceedingsTitle: (row) => Commons.EncryptAES(row.proceedingsTitle || ''),
      State: (row) => Commons.EncryptAES(row.state || '0'),
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    save2: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
      Gubun: (row) => Commons.EncryptAES(row.gubun || '0'),
      Id: (row) => Commons.EncryptAES(row.id || ''),
      DepNm: (row) => Commons.EncryptAES(row.depNm || ''),
      Rank: (row) => Commons.EncryptAES(row.rank || ''),
      Name: (row) => Commons.EncryptAES(row.name || ''),
      Tel: (row) => Commons.EncryptAES(row.tel || ''),
      AttendYn: (row) => Commons.EncryptAES(row.attendYn || 'N'),
      CheckYn: (row) => Commons.EncryptAES(row.checkYn || 'N'),
      CheckPath: (row) => Commons.EncryptAES(row.checkPath || ''),
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    save3: {
      Gubun: (row) => Commons.EncryptAES(row.gubun || '0'),
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
      Seq: (row) => Commons.EncryptAES(row.seq?.toString() || ''),
      AgendaName: (row) => Commons.EncryptAES(row.agendaName || ''),
      ResultDiscussion: (row) => Commons.EncryptAES(row.resultDiscussion || ''),
      Person: (row) => Commons.EncryptAES(row.person || ''),
      ProcessingDeadline: (row) =>
        Commons.EncryptAES(row.processingDeadline || ''),
      ProceedingsImage: (row) => Commons.EncryptAES(row.proceedingsImage || ''),
      ProceedingsImageNm: (row) =>
        Commons.EncryptAES(row.proceedingsImageNm || ''),
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    save4: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
      State: (row) => Commons.EncryptAES(row.state || '1'),
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    delete1: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
    },

    delete2: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
    },

    delete3: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
      Seq: (row) => Commons.EncryptAES(row.seq?.toString() || ''),
    },
  },
};
